// InvoiceEditor.jsx - Dedicated Invoice Editor Component
// Provides full control over invoice line items with auto-calculation

import React, { useState, useEffect, useCallback } from 'react'
import {
  X, Save, Plus, Trash2, RefreshCw, DollarSign,
  Calendar, Package, Calculator, AlertTriangle, CheckCircle
} from 'lucide-react'
import { clientAdminApi } from '../services/api'
import './InvoiceEditor.css'

function InvoiceEditor({ invoice, onClose, onSave }) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [fetchingRate, setFetchingRate] = useState(false)

  // Invoice data
  const [issueDate, setIssueDate] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [exchangeRate, setExchangeRate] = useState(280)
  const [notes, setNotes] = useState('')
  const [lineItems, setLineItems] = useState([])

  // Calculated totals
  const [totals, setTotals] = useState({ subtotal: 0, totalBots: 0, totalPkr: 0 })

  // Toast state
  const [toast, setToast] = useState(null)

  // Initialize from invoice
  useEffect(() => {
    if (invoice) {
      setIssueDate(invoice.issue_date?.split('T')[0] || '')
      setDueDate(invoice.due_date?.split('T')[0] || '')
      setExchangeRate(parseFloat(invoice.exchange_rate) || 280)
      setNotes(invoice.notes || '')

      // Parse line items
      let items = invoice.line_items || []
      if (typeof items === 'string') {
        try {
          items = JSON.parse(items)
        } catch (e) {
          items = []
        }
      }

      // Ensure each item has all required fields
      const normalizedItems = items.map((item, idx) => ({
        id: idx,
        description: item.description || `Bots Deployed on ${item.dialer_name || item.campaign || 'Unknown'}`,
        campaign: item.campaign || item.dialer_name || '',
        dialer_id: item.dialer_id || null,
        dialer_group: item.dialer_group || null,
        qty: parseInt(item.qty) || parseInt(item.bot_count) || 0,
        rate: parseFloat(item.rate) || parseFloat(item.price_per_bot) || 0,
        days: parseInt(item.days) || 0,
        date_from: item.date_from?.split('T')[0] || invoice.period_start?.split('T')[0] || '',
        date_to: item.date_to?.split('T')[0] || invoice.period_end?.split('T')[0] || '',
        amount_usd: parseFloat(item.amount_usd) || 0,
        is_previous_balance: item.is_previous_balance || false,
        is_segment: item.is_segment || false,
        previous_invoice_no: item.previous_invoice_no || null
      }))

      setLineItems(normalizedItems)
    }
  }, [invoice])

  // Calculate totals when line items or exchange rate changes
  useEffect(() => {
    const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount_usd) || 0), 0)
    const totalBots = lineItems
      .filter(i => !i.is_previous_balance)
      .reduce((sum, item) => sum + (parseInt(item.qty) || 0), 0)
    const totalPkr = subtotal * parseFloat(exchangeRate)

    setTotals({ subtotal, totalBots, totalPkr })
  }, [lineItems, exchangeRate])

  // Calculate amount for a line item
  const calculateAmount = (qty, rate, days) => {
    const q = parseInt(qty) || 0
    const r = parseFloat(rate) || 0
    const d = parseInt(days) || 0
    return parseFloat(((q * r * d) / 30).toFixed(2))
  }

  // Calculate days from date range
  const calculateDays = (dateFrom, dateTo) => {
    if (!dateFrom || !dateTo) return 0
    const from = new Date(dateFrom)
    const to = new Date(dateTo)
    const diff = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1
    return diff > 0 ? diff : 0
  }

  // Update a line item field
  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }

      // Auto-calculate days if date range changed
      if (field === 'date_from' || field === 'date_to') {
        const days = calculateDays(
          field === 'date_from' ? value : updated[index].date_from,
          field === 'date_to' ? value : updated[index].date_to
        )
        updated[index].days = days
        // Recalculate amount
        updated[index].amount_usd = calculateAmount(
          updated[index].qty,
          updated[index].rate,
          days
        )
      }

      // Auto-calculate amount if qty, rate, or days changed
      if (field === 'qty' || field === 'rate' || field === 'days') {
        updated[index].amount_usd = calculateAmount(
          field === 'qty' ? value : updated[index].qty,
          field === 'rate' ? value : updated[index].rate,
          field === 'days' ? value : updated[index].days
        )
      }

      return updated
    })
  }

  // Add new line item
  const addLineItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      campaign: '',
      dialer_id: null,
      qty: 0,
      rate: 11.00,
      days: 30,
      date_from: invoice?.period_start?.split('T')[0] || '',
      date_to: invoice?.period_end?.split('T')[0] || '',
      amount_usd: 0,
      is_previous_balance: false
    }
    setLineItems(prev => [...prev, newItem])
  }

  // Remove line item locally - saved when clicking "Save Invoice"
  const removeLineItem = (index) => {
    if (!window.confirm('Remove this line item? Click "Save Invoice" to confirm.')) return
    setLineItems(prev => prev.filter((_, i) => i !== index))
    showToast('Line item removed (save to confirm)')
  }

  // Fetch latest exchange rate (force refresh to bypass cache)
  const fetchExchangeRate = async () => {
    setFetchingRate(true)
    try {
      const res = await clientAdminApi.getExchangeRate(true)  // Force fresh fetch
      if (res.data?.success && res.data.rate) {
        setExchangeRate(res.data.rate)
        showToast(`Exchange rate updated: ${res.data.rate.toFixed(2)} (${res.data.source})`)
      }
    } catch (error) {
      showToast('Failed to fetch exchange rate', 'error')
    }
    setFetchingRate(false)
  }

  // Show toast message
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Save invoice changes - single API call with all data
  const handleSave = async () => {
    setSaving(true)
    try {
      // Send everything in one call - backend handles line_items bulk replace
      await clientAdminApi.updateInvoice(invoice.id, {
        issue_date: issueDate,
        due_date: dueDate,
        exchange_rate: exchangeRate,
        notes: notes,
        line_items: lineItems.map(item => ({
          description: item.description,
          campaign: item.campaign || item.dialer_name,
          dialer_name: item.dialer_name || item.campaign,
          dialer_id: item.dialer_id,
          dialer_group: item.dialer_group,
          qty: item.qty,
          bot_count: item.bot_count || item.qty,
          rate: item.rate,
          price_per_bot: item.price_per_bot || item.rate,
          days: item.days,
          date_from: item.date_from,
          date_to: item.date_to,
          amount_usd: parseFloat(item.amount_usd) || 0,
          is_previous_balance: item.is_previous_balance || false,
          is_segment: item.is_segment || false
        }))
      })

      showToast('Invoice updated successfully')
      if (onSave) onSave()
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to save invoice', 'error')
    }
    setSaving(false)
  }

  return (
    <div className="invoice-editor-overlay" onClick={onClose}>
      <div className="invoice-editor" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="ie-header">
          <div className="ie-header-title">
            <DollarSign size={20} />
            <h2>Edit Invoice - {invoice?.invoice_no}</h2>
          </div>
          <button className="ie-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`ie-toast ${toast.type}`}>
            {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
            {toast.message}
          </div>
        )}

        {/* Content */}
        <div className="ie-content">
          {/* Invoice Details */}
          <div className="ie-details-section">
            <div className="ie-details-row">
              <div className="ie-field">
                <label>Issue Date</label>
                <input
                  type="date"
                  value={issueDate}
                  onChange={e => setIssueDate(e.target.value)}
                />
              </div>
              <div className="ie-field">
                <label>Due Date</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                />
              </div>
              <div className="ie-field ie-exchange-rate">
                <label>Exchange Rate (PKR)</label>
                <div className="ie-rate-input">
                  <input
                    type="number"
                    step="0.01"
                    value={exchangeRate}
                    onChange={e => setExchangeRate(parseFloat(e.target.value) || 280)}
                  />
                  <button
                    type="button"
                    className="ie-fetch-rate-btn"
                    onClick={fetchExchangeRate}
                    disabled={fetchingRate}
                    title="Fetch latest rate"
                  >
                    <RefreshCw size={14} className={fetchingRate ? 'spin' : ''} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="ie-line-items-section">
            <div className="ie-section-header">
              <h3>Line Items</h3>
              <button className="ie-add-btn" onClick={addLineItem}>
                <Plus size={14} /> Add Line Item
              </button>
            </div>

            <div className="ie-line-items-table-wrapper">
              <table className="ie-line-items-table">
                <thead>
                  <tr>
                    <th className="ie-col-desc">Description</th>
                    <th className="ie-col-campaign">Campaign</th>
                    <th className="ie-col-qty">QTY</th>
                    <th className="ie-col-rate">Rate ($)</th>
                    <th className="ie-col-dates">Date Range</th>
                    <th className="ie-col-days">Days</th>
                    <th className="ie-col-amount">Amount ($)</th>
                    <th className="ie-col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const hasGroups = lineItems.some(i => i.dialer_group && !i.is_previous_balance);

                    if (hasGroups) {
                      // Grouped view: render items grouped by dialer_group
                      const groups = {};
                      const prevBalanceItems = [];
                      const rows = [];

                      lineItems.forEach((item, index) => {
                        if (item.is_previous_balance) {
                          prevBalanceItems.push({ item, index });
                          return;
                        }
                        const group = item.dialer_group || item.campaign || 'Other';
                        if (!groups[group]) groups[group] = [];
                        groups[group].push({ item, index });
                      });

                      Object.entries(groups).forEach(([groupName, groupItems]) => {
                        const groupTotal = groupItems.reduce((sum, { item }) => sum + (parseFloat(item.amount_usd) || 0), 0);
                        const groupRate = groupItems[0]?.item.rate || 0;

                        // Group header
                        rows.push(
                          <tr key={`group-${groupName}`} className="ie-group-header">
                            <td colSpan="4">
                              <strong>{groupName}</strong>
                            </td>
                            <td colSpan="3" style={{ textAlign: 'right', fontSize: '11px', color: 'var(--text-muted)' }}>
                              Rate: ${parseFloat(groupRate).toFixed(2)}/bot &nbsp; | &nbsp; Subtotal: ${groupTotal.toFixed(2)}
                            </td>
                            <td></td>
                          </tr>
                        );

                        // Group items
                        groupItems.forEach(({ item, index }) => {
                          rows.push(
                            <tr key={item.id || index} className="ie-segment-row">
                              <td className="ie-col-desc">
                                <input
                                  type="text"
                                  value={item.description}
                                  onChange={e => updateLineItem(index, 'description', e.target.value)}
                                  placeholder="Bots Deployed on..."
                                />
                              </td>
                              <td className="ie-col-campaign">
                                <input
                                  type="text"
                                  value={item.campaign}
                                  onChange={e => updateLineItem(index, 'campaign', e.target.value)}
                                  placeholder="Campaign"
                                />
                              </td>
                              <td className="ie-col-qty">
                                <input
                                  type="number"
                                  value={item.qty}
                                  onChange={e => updateLineItem(index, 'qty', parseInt(e.target.value) || 0)}
                                />
                              </td>
                              <td className="ie-col-rate">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={item.rate}
                                  onChange={e => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)}
                                />
                              </td>
                              <td className="ie-col-dates">
                                <div className="ie-date-range">
                                  <input type="date" value={item.date_from} onChange={e => updateLineItem(index, 'date_from', e.target.value)} />
                                  <span>to</span>
                                  <input type="date" value={item.date_to} onChange={e => updateLineItem(index, 'date_to', e.target.value)} />
                                </div>
                              </td>
                              <td className="ie-col-days">
                                <input
                                  type="number"
                                  value={item.days}
                                  onChange={e => updateLineItem(index, 'days', parseInt(e.target.value) || 0)}
                                />
                              </td>
                              <td className="ie-col-amount">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={item.amount_usd}
                                  onChange={e => updateLineItem(index, 'amount_usd', parseFloat(e.target.value) || 0)}
                                  className="ie-amount-input"
                                />
                              </td>
                              <td className="ie-col-actions">
                                <button type="button" className="ie-delete-btn" onClick={() => removeLineItem(index)} title="Remove">
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          );
                        });
                      });

                      // Previous balance items
                      if (prevBalanceItems.length > 0) {
                        rows.push(
                          <tr key="prev-header" className="ie-group-header ie-prev-group">
                            <td colSpan="8"><strong>Previous Balance</strong></td>
                          </tr>
                        );
                        prevBalanceItems.forEach(({ item, index }) => {
                          rows.push(
                            <tr key={item.id || index} className="ie-previous-balance-row">
                              <td className="ie-col-desc">
                                <input type="text" value={item.description} onChange={e => updateLineItem(index, 'description', e.target.value)} />
                                <span className="ie-prev-badge">Previous Balance</span>
                              </td>
                              <td className="ie-col-campaign">
                                <input type="text" value={item.campaign} onChange={e => updateLineItem(index, 'campaign', e.target.value)} />
                              </td>
                              <td className="ie-col-qty"><input type="number" value={item.qty} disabled /></td>
                              <td className="ie-col-rate"><input type="number" value={item.rate} disabled /></td>
                              <td className="ie-col-dates">
                                <div className="ie-date-range">
                                  <input type="date" value={item.date_from} onChange={e => updateLineItem(index, 'date_from', e.target.value)} />
                                  <span>to</span>
                                  <input type="date" value={item.date_to} onChange={e => updateLineItem(index, 'date_to', e.target.value)} />
                                </div>
                              </td>
                              <td className="ie-col-days"><input type="number" value={item.days} disabled /></td>
                              <td className="ie-col-amount">
                                <input type="number" step="0.01" value={item.amount_usd} onChange={e => updateLineItem(index, 'amount_usd', parseFloat(e.target.value) || 0)} className="ie-amount-input" />
                              </td>
                              <td className="ie-col-actions">
                                <button type="button" className="ie-delete-btn" onClick={() => removeLineItem(index)} title="Remove">
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          );
                        });
                      }

                      return rows;
                    }

                    // Flat view (no groups) - original behavior
                    return lineItems.map((item, index) => (
                      <tr key={item.id || index} className={item.is_previous_balance ? 'ie-previous-balance-row' : ''}>
                        <td className="ie-col-desc">
                          <input
                            type="text"
                            value={item.description}
                            onChange={e => updateLineItem(index, 'description', e.target.value)}
                            placeholder="Bots Deployed on..."
                          />
                          {item.is_previous_balance && (
                            <span className="ie-prev-badge">Previous Balance</span>
                          )}
                        </td>
                        <td className="ie-col-campaign">
                          <input
                            type="text"
                            value={item.campaign}
                            onChange={e => updateLineItem(index, 'campaign', e.target.value)}
                            placeholder="Campaign"
                          />
                        </td>
                        <td className="ie-col-qty">
                          <input
                            type="number"
                            value={item.qty}
                            onChange={e => updateLineItem(index, 'qty', parseInt(e.target.value) || 0)}
                            disabled={item.is_previous_balance}
                          />
                        </td>
                        <td className="ie-col-rate">
                          <input
                            type="number"
                            step="0.01"
                            value={item.rate}
                            onChange={e => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)}
                            disabled={item.is_previous_balance}
                          />
                        </td>
                        <td className="ie-col-dates">
                          <div className="ie-date-range">
                            <input
                              type="date"
                              value={item.date_from}
                              onChange={e => updateLineItem(index, 'date_from', e.target.value)}
                            />
                            <span>to</span>
                            <input
                              type="date"
                              value={item.date_to}
                              onChange={e => updateLineItem(index, 'date_to', e.target.value)}
                            />
                          </div>
                        </td>
                        <td className="ie-col-days">
                          <input
                            type="number"
                            value={item.days}
                            onChange={e => updateLineItem(index, 'days', parseInt(e.target.value) || 0)}
                            disabled={item.is_previous_balance}
                          />
                        </td>
                        <td className="ie-col-amount">
                          <input
                            type="number"
                            step="0.01"
                            value={item.amount_usd}
                            onChange={e => updateLineItem(index, 'amount_usd', parseFloat(e.target.value) || 0)}
                            className="ie-amount-input"
                          />
                        </td>
                        <td className="ie-col-actions">
                          <button
                            type="button"
                            className="ie-delete-btn"
                            onClick={() => removeLineItem(index)}
                            title="Remove line item"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ));
                  })()}
                  {lineItems.length === 0 && (
                    <tr>
                      <td colSpan="8" className="ie-empty-state">
                        <Package size={24} />
                        <p>No line items. Click "Add Line Item" to add one.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="ie-totals-section">
            <div className="ie-totals-grid">
              <div className="ie-total-item">
                <span className="ie-total-label">Total Bots</span>
                <span className="ie-total-value">{totals.totalBots}</span>
              </div>
              <div className="ie-total-item">
                <span className="ie-total-label">Subtotal (USD)</span>
                <span className="ie-total-value ie-amount">${totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="ie-total-item ie-total-pkr">
                <span className="ie-total-label">Total (PKR)</span>
                <span className="ie-total-value ie-amount">Rs. {totals.totalPkr.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="ie-notes-section">
            <label>Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Internal notes..."
              rows={3}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="ie-footer">
          <button className="ie-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="ie-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <RefreshCw size={16} className="spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Invoice
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default InvoiceEditor
